import type { PublicRuntimeConfig } from 'nuxt/schema'

/**
 * This composable injects scripts into the head of the page.
 */
export default async function useScripts() {
  const runtimeConfig = useRuntimeConfig()
  const route = useRoute()
  const { user } = useUser()
  const sessionCookie = useRtsessionIdCookie()
  const { getAllVariants, getConfigurationValue, isFeatureEnabled } = useExperiment()
  const { isNotSupported } = useBrowserSupport()
  const { isPotentiallyABot } = useBotScore()

  const publicConfig = runtimeConfig.public

  useHead({
    script: [
      ...injectScripts({
        $config: publicConfig,
        $user: user,
        $query: route.query,
        isPotentiallyABot,
        isNotSupported,
      }),
    ],
  })

  /**
   * Generates the head scripts to inject.
   */

  function injectScripts({ $config, $user, $query, isPotentiallyABot, isNotSupported }: { $config: PublicRuntimeConfig, $user: typeof user, $query: typeof route.query, isPotentiallyABot: boolean, isNotSupported: boolean }) {
    if (isPotentiallyABot) {
      return []
    }

    // Ugly hack in order to prevent the scripts from being injected twice on
    // the client. Perhaps this can removed once scripts are properly handled.
    if (isMetaClient && window?.injected) {
      return []
    }

    // Filter undefined/disabled scripts
    return [
      injectDatadog({
        experiments: getAllVariants(),
        isEnabled: isFeatureEnabled('rvz10584DatadogRumEnabled'),
        isNotSupported,
        sampleRate: Number(getConfigurationValue('rvz11609DatadogRumSampleRate') ?? 0),
        rtSessionId: sessionCookie.value ?? '',
        $config: $config.datadog.common,
        userRole: $user.value?.IsOwner ? 'owner' : 'user',
      }),
      injectSegment({
        isEnabled: $config.segment.enabled,
        rtSessionId: sessionCookie.value ?? '',
        url: $config.segment.url,
        writeKey: $config.segment.writeKey,
      }),
      injectSegmentPlugin({
        isEnabled: $config.segment.enabled,
      }),
      injectGtag({
        isEnabled: $config.gtag.enabled,
        analyticsId: $config.googleAnalytics.id,
        adsId: $config.googleAds.id,
        userId: $user.value?.Id.toString() ?? '',
        userEmail: $user.value?.Email ?? '',
        query: $query,
      }),
      injectGtagBase({
        isEnabled: $config.gtag.enabled,
        id: $config.googleAnalytics.id,
      }),
      injectOpenAi({
        pixelId: $config.openai.id,
        enabled: $config.openai.enabled,
        debugMode: $config.openai.debugMode,
      }),
      injectClarity({
        isEnabled: isFeatureEnabled('rvz20755clarityFeatureFlag') && $config.microsoftClarity.enabled,
        id: $config.microsoftClarity.id,
      }),
      injectTheTradeDeskBase({
        isEnabled: $config.theTradeDesk.enabled,
      }),
      injectTheTradeDesk({
        isEnabled: $config.theTradeDesk.enabled,
        advertiserId: $config.theTradeDesk.advertiserId,
        uPixelId: $config.theTradeDesk.uPixelId,
      }),
      injectAdalyser({
        isEnabled: $config.adalyser.enabled,
        id: $config.adalyser.id,
      }),
      {
        textContent: `injected = true;`,
      },
    ].flatMap((item) => item || [])
  }
}
